import { useEffect, useState, useCallback, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, BadgeCheck, Building2, Phone, Mail,
  IndianRupee, Calendar, User, RefreshCw, Plus, Edit2,
  CheckCircle2, Clock, AlertCircle, RotateCcw,
  Pencil, Trash2, X, Check, LifeBuoy, Receipt, Wallet,
  Package, Sparkles, PlusCircle, FileText, StickyNote, Info,
  ClipboardList, Link2, Users,
} from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { AuthContext } from "../../context/AuthContext";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { State, City } from "country-state-city";
import MilestonesPanel from "../../components/onboarded/MilestonesPanel";
import {
  ONBOARDED_DETAIL_URL, ONBOARDED_AMC_URL, ONBOARDED_AMC_DETAIL_URL,
  ONBOARDED_AMC_RENEW_URL, ONBOARDED_AMC_HISTORY_URL,
  ONBOARDED_CONTACTS_URL, ONBOARDED_CONTACT_URL,
  ONBOARDED_TICKETS_URL, ONBOARDED_TICKET_URL,
  ONBOARDED_PRODUCT_RECEIPTS_URL, ONBOARDED_PRODUCT_RECEIPT_URL,
  ONBOARDED_AMC_RECEIPTS_URL, ONBOARDED_AMC_RECEIPT_URL,
  ONBOARDED_NOTES_URL, ONBOARDED_NOTE_URL,
  USERS_SIMPLE_URL, CUSTOMER_TYPE_MASTER_URL,
} from "../../constants/api";

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtCurrency = (v) =>
  v ? `₹${parseFloat(v).toLocaleString("en-IN")}` : "—";
const daysLeft = (d) =>
  d ? Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24)) : null;

const STATUS_COLOR = {
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "Go Live":     "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Completed":   "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  "Suspended":   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};
const AMC_COLOR = {
  "Paid":    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Pending": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  "Overdue": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  "Waived":  "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};
const TICKET_PRIORITY_COLOR = {
  "Low":      "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  "Medium":   "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "High":     "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  "Critical": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};
const TICKET_STATUS_COLOR = {
  "Open":        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "Resolved":    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Closed":      "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  "On Hold":     "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
};

const card = "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700";

const LabelVal = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value || "—"}</p>
  </div>
);

// One recorded handover answer. Rendered the way the sales panel renders it so
// a CRE reading this screen and a rep reading the lead see the same thing.
// Display only — the CRE Handover tab on the lead is the single writer.
const HandoverValue = ({ f }) => {
  const v = f.value;
  if (v === "" || v === null || v === undefined || (Array.isArray(v) && !v.length))
    return <span className="text-gray-300 dark:text-gray-600">—</span>;

  if (f.type === "boolean")
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${
        v ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
          : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}>
        {v ? "Yes" : "No"}
      </span>
    );
  if (f.type === "date")  return <>{fmtDate(v)}</>;
  if (f.type === "money") return <>{fmtCurrency(v)}</>;
  if (f.type === "multi")
    return (
      <span className="flex flex-wrap gap-1">
        {v.map(x => (
          <span key={x} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-[11px]">
            {x}
          </span>
        ))}
      </span>
    );
  if (f.type === "textarea")
    return <span className="whitespace-pre-wrap">{String(v)}</span>;
  return <>{String(v)}{f.unit && <span className="text-gray-400 ml-0.5">{f.unit}</span>}</>;
};

const LabelInput = ({ label, required, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
  </div>
);

// Handover sits second on purpose. It is what the implementation team reads
// before anything else — what was sold, what was promised, and who to ask —
// and burying it behind the money tabs is how a CRE ends up guessing.
const TABS = ["Overview", "Handover", "Modules", "Product Payments", "AMC & Renewals", "Milestones", "Contacts", "Notes", "Support"];

const PAYMENT_MODES = ["Cash", "Cheque", "UPI", "NEFT", "RTGS", "Card", "Other"];
const EMPTY_RECEIPT = { receipt_date: "", amount: "", receipt_number: "", payment_mode: "Other", reference_no: "", notes: "" };
const EMPTY_AMC = {
  amc_start_date: "", amc_end_date: "", amc_value: "", amc_percentage: "",
  payment_terms: "Annual", payment_status: "Pending", renewal_reminder_days: "30",
};

// ── Component ──────────────────────────────────────────────────────────────────
export default function OnboardedDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { fetch, create, update, remove } = useApiMethods();
  const { hasPermission } = useContext(AuthContext);

  // Every write on this page goes through the `onboarded` module, and the
  // backend enforces the action separately (see AMCPaymentReceiptDetailView.put,
  // which needs 'edit'). The page used to offer Add / Edit / Delete to everyone,
  // so a view-only role — 'accounts', the very people entering receipts — could
  // fill in a receipt form and only find out it was refused on save.
  const isSuperAdmin = sessionStorage.getItem("login_role") === "super_admin";
  const may = (action) => isSuperAdmin || hasPermission("onboarded", action);
  const canCreate = may("create");
  const canEdit   = may("edit");
  const canDelete = may("delete");

  const [client,      setClient]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState(location.state?.tab || "Overview");
  const [users,       setUsers]       = useState([]);
  const [clientTypes, setClientTypes] = useState([]);

  // AMC state
  const [amcRecords,     setAmcRecords]     = useState([]);
  const [showAddAmc,     setShowAddAmc]     = useState(false);
  const [showRenewId,    setShowRenewId]    = useState(null);
  const [renewalHistory, setRenewalHistory] = useState({});
  const [editingAmcId,   setEditingAmcId]   = useState(null);
  const [amcForm,        setAmcForm]        = useState(EMPTY_AMC);
  const [editAmcForm,    setEditAmcForm]    = useState(EMPTY_AMC);
  const [renewForm, setRenewForm] = useState({ new_end_date: "", renewal_value: "", remarks: "" });

  // Milestone state. Only the list is held here, for the Overview progress
  // bar — the Milestones tab owns its own editing state in MilestonesPanel.
  const [milestones,  setMilestones]  = useState([]);

  // Contacts state
  const [contacts,         setContacts]         = useState([]);
  const [showAddContact,   setShowAddContact]   = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm,      setContactForm]      = useState({ name: "", designation: "", contact_number: "", email: "", is_primary: false });

  // Edit client state
  const [editingClient,   setEditingClient]   = useState(false);
  const [editClientForm,  setEditClientForm]  = useState({});
  const [savingClient,    setSavingClient]    = useState(false);

  // Support ticket state
  const [tickets,         setTickets]         = useState([]);
  const [ticketsLoaded,   setTicketsLoaded]   = useState(false);
  const [showAddTicket,   setShowAddTicket]   = useState(false);
  const [ticketForm,      setTicketForm]      = useState({ title: "", description: "", priority: "Medium", assigned_to_id: "" });
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [editTicketForm,  setEditTicketForm]  = useState({});

  // Product payment receipts
  const [productReceipts,       setProductReceipts]       = useState([]);
  const [productSummary,        setProductSummary]        = useState(null);
  const [productReceiptsLoaded, setProductReceiptsLoaded] = useState(false);
  const [showAddProductReceipt, setShowAddProductReceipt] = useState(false);
  const [productReceiptForm,    setProductReceiptForm]    = useState(EMPTY_RECEIPT);
  const [editingProductReceiptId, setEditingProductReceiptId] = useState(null);

  // AMC receipts — keyed by amc_id
  const [amcReceipts,        setAmcReceipts]        = useState({}); // { amcId: { data: [], summary: {} } }
  const [amcReceiptsLoaded,  setAmcReceiptsLoaded]  = useState({}); // { amcId: true }
  const [showAmcReceiptFor,  setShowAmcReceiptFor]  = useState(null);
  const [amcReceiptForm,     setAmcReceiptForm]     = useState(EMPTY_RECEIPT);
  const [editingAmcReceiptId, setEditingAmcReceiptId] = useState(null);
  const [expandedAmcId,      setExpandedAmcId]      = useState(null);

  // Notes
  const [notes,         setNotes]         = useState([]);
  const [notesLoaded,   setNotesLoaded]   = useState(false);
  const [noteDraft,     setNoteDraft]     = useState("");
  const [savingNote,    setSavingNote]    = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText,  setEditNoteText]  = useState("");

  const loadTickets = useCallback(async () => {
    try {
      const res = await fetch(ONBOARDED_TICKETS_URL(id));
      if (res?.status === "success") { setTickets(res.data || []); setTicketsLoaded(true); }
    } catch { ToastMsg("Failed to load tickets", "error"); }
  }, [id]);

  const loadNotes = useCallback(async () => {
    try {
      const res = await fetch(ONBOARDED_NOTES_URL(id));
      if (res?.status === "success") { setNotes(res.data || []); setNotesLoaded(true); }
    } catch { ToastMsg("Failed to load notes", "error"); }
  }, [id]);

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text) { ToastMsg("Note cannot be empty", "error"); return; }
    setSavingNote(true);
    const res = await create(ONBOARDED_NOTES_URL(id), { note: text });
    setSavingNote(false);
    if (res?.status === "success") {
      ToastMsg("Note added", "success");
      setNoteDraft("");
      loadNotes();
    } else {
      ToastMsg(res?.message || "Failed to add note", "error");
    }
  };

  const saveEditedNote = async (nid) => {
    const text = editNoteText.trim();
    if (!text) { ToastMsg("Note cannot be empty", "error"); return; }
    const res = await update(ONBOARDED_NOTE_URL(nid), { note: text });
    if (res?.status === "success") {
      ToastMsg("Note updated", "success");
      setEditingNoteId(null);
      setEditNoteText("");
      loadNotes();
    } else {
      ToastMsg(res?.message || "Failed to update note", "error");
    }
  };

  const deleteNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    const res = await remove(ONBOARDED_NOTE_URL(nid));
    if (res?.status === "success") { ToastMsg("Note deleted", "success"); loadNotes(); }
    else ToastMsg(res?.message || "Failed to delete", "error");
  };

  const loadProductReceipts = useCallback(async () => {
    try {
      const res = await fetch(ONBOARDED_PRODUCT_RECEIPTS_URL(id));
      if (res?.status === "success") {
        setProductReceipts(res.data || []);
        setProductSummary(res.summary || null);
        setProductReceiptsLoaded(true);
      }
    } catch { ToastMsg("Failed to load payments", "error"); }
  }, [id]);

  const loadAmcReceipts = useCallback(async (amcId) => {
    try {
      const res = await fetch(ONBOARDED_AMC_RECEIPTS_URL(amcId));
      if (res?.status === "success") {
        setAmcReceipts(prev => ({ ...prev, [amcId]: { data: res.data || [], summary: res.summary || {} } }));
        setAmcReceiptsLoaded(prev => ({ ...prev, [amcId]: true }));
      }
    } catch { ToastMsg("Failed to load AMC receipts", "error"); }
  }, []);

  const saveProductReceipt = async () => {
    if (!productReceiptForm.receipt_date || !productReceiptForm.amount) {
      ToastMsg("Receipt date and amount are required", "error");
      return;
    }
    const isEdit = !!editingProductReceiptId;
    const res = isEdit
      ? await update(ONBOARDED_PRODUCT_RECEIPT_URL(editingProductReceiptId), productReceiptForm)
      : await create(ONBOARDED_PRODUCT_RECEIPTS_URL(id), productReceiptForm);
    if (res?.status === "success") {
      ToastMsg(isEdit ? "Receipt updated" : "Receipt added", "success");
      setProductReceiptForm(EMPTY_RECEIPT);
      setShowAddProductReceipt(false);
      setEditingProductReceiptId(null);
      loadProductReceipts();
    } else {
      ToastMsg(res?.message || `Failed to ${isEdit ? "update" : "add"} receipt`, "error");
    }
  };

  const startEditProductReceipt = (r) => {
    setProductReceiptForm({
      receipt_date: r.receipt_date || "",
      amount: r.amount ?? "",
      receipt_number: r.receipt_number || "",
      payment_mode: r.payment_mode || "Other",
      reference_no: r.reference_no || "",
      notes: r.notes || "",
    });
    setEditingProductReceiptId(r.id);
    setShowAddProductReceipt(true);
  };

  const deleteProductReceipt = async (rid) => {
    if (!window.confirm("Delete this receipt?")) return;
    const res = await remove(ONBOARDED_PRODUCT_RECEIPT_URL(rid));
    if (res?.status === "success") { ToastMsg("Receipt deleted", "success"); loadProductReceipts(); }
    else ToastMsg(res?.message || "Failed to delete", "error");
  };

  const saveAmcReceipt = async (amcId) => {
    if (!amcReceiptForm.receipt_date || !amcReceiptForm.amount) {
      ToastMsg("Receipt date and amount are required", "error");
      return;
    }
    const isEdit = !!editingAmcReceiptId;
    const res = isEdit
      ? await update(ONBOARDED_AMC_RECEIPT_URL(editingAmcReceiptId), amcReceiptForm)
      : await create(ONBOARDED_AMC_RECEIPTS_URL(amcId), amcReceiptForm);
    if (res?.status === "success") {
      ToastMsg(isEdit ? "AMC receipt updated" : "AMC receipt added", "success");
      setAmcReceiptForm(EMPTY_RECEIPT);
      setShowAmcReceiptFor(null);
      setEditingAmcReceiptId(null);
      loadAmcReceipts(amcId);
      loadClient();
    } else {
      ToastMsg(res?.message || `Failed to ${isEdit ? "update" : "add"} receipt`, "error");
    }
  };

  const startEditAmcReceipt = (r, amcId) => {
    setAmcReceiptForm({
      receipt_date: r.receipt_date || "",
      amount: r.amount ?? "",
      receipt_number: r.receipt_number || "",
      payment_mode: r.payment_mode || "Other",
      reference_no: r.reference_no || "",
      notes: r.notes || "",
    });
    setEditingAmcReceiptId(r.id);
    setShowAmcReceiptFor(amcId);
    if (expandedAmcId !== amcId) setExpandedAmcId(amcId);
  };

  const deleteAmcReceipt = async (rid, amcId) => {
    if (!window.confirm("Delete this receipt?")) return;
    const res = await remove(ONBOARDED_AMC_RECEIPT_URL(rid));
    if (res?.status === "success") {
      ToastMsg("Receipt deleted", "success");
      loadAmcReceipts(amcId);
      loadClient();
    } else ToastMsg(res?.message || "Failed to delete", "error");
  };

  const loadClient = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ONBOARDED_DETAIL_URL(id));
      if (res?.status === "success") {
        setClient(res.data);
        setAmcRecords(res.data.amc_records || []);
        setMilestones(res.data.milestones || []);
        setContacts(res.data.contacts || []);
      }
    } catch {
      ToastMsg("Failed to load client", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadClient(); }, [loadClient]);
  useEffect(() => {
    if (activeTab === "Support" && !ticketsLoaded) loadTickets();
    if (activeTab === "Product Payments" && !productReceiptsLoaded) loadProductReceipts();
    if (activeTab === "Notes" && !notesLoaded) loadNotes();
  }, [activeTab, ticketsLoaded, loadTickets, productReceiptsLoaded, loadProductReceipts, notesLoaded, loadNotes]);
  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then(res => {
      if (res?.status === "success") setUsers(res.data || []);
    });
    fetch(CUSTOMER_TYPE_MASTER_URL).then(res => {
      if (res?.status === "success")
        setClientTypes((res.data || []).filter(t => !t.is_channel_partner && t.is_active !== false));
    });
  }, []);

  const userOptions = users.map(u => ({
    value: String(u.id),
    label: `${u.login_fname || ""} ${u.login_lname || ""}`.trim(),
  }));

  // ── AMC actions ───────────────────────────────────────────────────
  const saveAmc = async () => {
    if (!amcForm.amc_start_date || !amcForm.amc_end_date)
      return ToastMsg("Start and end dates are required", "error");
    const res = await create(ONBOARDED_AMC_URL(id), amcForm);
    if (res?.status === "success") {
      ToastMsg("AMC record added", "success");
      setShowAddAmc(false);
      setAmcForm(EMPTY_AMC);
      loadClient();
    }
  };

  const startEditAmc = (amc) => {
    setEditAmcForm({
      amc_start_date:        amc.amc_start_date || "",
      amc_end_date:          amc.amc_end_date || "",
      amc_value:             amc.amc_value ?? "",
      amc_percentage:        amc.amc_percentage ?? "",
      payment_terms:         amc.payment_terms || "Annual",
      payment_status:        amc.payment_status || "Pending",
      renewal_reminder_days: amc.renewal_reminder_days ?? "30",
    });
    setEditingAmcId(amc.id);
    setShowAddAmc(false);
    setShowRenewId(null);
  };

  const cancelEditAmc = () => {
    setEditingAmcId(null);
    setEditAmcForm(EMPTY_AMC);
  };

  const updateAmc = async () => {
    if (!editAmcForm.amc_start_date || !editAmcForm.amc_end_date)
      return ToastMsg("Start and end dates are required", "error");
    const res = await update(ONBOARDED_AMC_DETAIL_URL(editingAmcId), editAmcForm);
    if (res?.status === "success") {
      ToastMsg("AMC record updated", "success");
      cancelEditAmc();
      loadClient();
    }
  };

  const renewAmc = async (amcId) => {
    if (!renewForm.new_end_date) return ToastMsg("New end date is required", "error");
    const res = await create(ONBOARDED_AMC_RENEW_URL(amcId), renewForm);
    if (res?.status === "success") {
      ToastMsg("AMC renewed", "success");
      setShowRenewId(null);
      setRenewForm({ new_end_date: "", renewal_value: "", remarks: "" });
      loadClient();
    }
  };

  const loadRenewalHistory = async (amcId) => {
    if (renewalHistory[amcId]) {
      setRenewalHistory(prev => ({ ...prev, [amcId]: null }));
      return;
    }
    const res = await fetch(ONBOARDED_AMC_HISTORY_URL(amcId));
    if (res?.status === "success")
      setRenewalHistory(prev => ({ ...prev, [amcId]: res.data }));
  };


  // ── Contact actions ───────────────────────────────────────────────
  const openAddContact = () => {
    setEditingContactId(null);
    setContactForm({ name: "", designation: "", contact_number: "", email: "", is_primary: false });
    setShowAddContact(true);
  };
  const openEditContact = (c) => {
    setEditingContactId(c.id);
    setContactForm({
      name: c.name || "",
      designation: c.designation || "",
      contact_number: c.contact_number || "",
      email: c.email || "",
      is_primary: !!c.is_primary,
    });
    setShowAddContact(true);
  };
  const cancelContact = () => {
    setShowAddContact(false);
    setEditingContactId(null);
    setContactForm({ name: "", designation: "", contact_number: "", email: "", is_primary: false });
  };

  const saveContact = async () => {
    if (!contactForm.name.trim()) return ToastMsg("Contact name is required", "error");
    const res = editingContactId
      ? await update(ONBOARDED_CONTACT_URL(editingContactId), contactForm)
      : await create(ONBOARDED_CONTACTS_URL(id), contactForm);
    if (res?.status === "success") {
      ToastMsg(editingContactId ? "Contact updated" : "Contact added", "success");
      cancelContact();
      loadClient();
    }
  };

  const deleteContact = async (cid) => {
    const res = await remove(ONBOARDED_CONTACT_URL(cid));
    if (res?.status === "success") { ToastMsg("Contact removed", "success"); loadClient(); }
  };

  // ── Client edit actions ───────────────────────────────────────
  const startEditClient = () => {
    setEditClientForm({
      org_name:                 client.org_name || "",
      client_type:              client.client_type || "",
      city:                     client.city || "",
      state:                    client.state || "",
      address:                  client.address || "",
      onboarding_status:        client.onboarding_status || "In Progress",
      onboarded_date:           client.onboarded_date || "",
      go_live_date:             client.go_live_date || "",
      assigned_to_id:           client.assigned_to_id ? String(client.assigned_to_id) : "",
      implementation_owner_id:  client.implementation_owner_id ? String(client.implementation_owner_id) : "",
      remarks:                  client.remarks || "",
      product_sold:             client.product_sold || "",
      contract_value:           client.contract_value || "",
      payment_terms:            client.payment_terms || "",
      contract_period:          client.contract_period || "",
    });
    setEditingClient(true);
  };

  const saveClientDetails = async () => {
    if (!editClientForm.org_name.trim()) return ToastMsg("Organization name is required", "error");
    setSavingClient(true);
    const res = await update(ONBOARDED_DETAIL_URL(id), {
      ...editClientForm,
      assigned_to_id:          editClientForm.assigned_to_id || null,
      implementation_owner_id: editClientForm.implementation_owner_id || null,
    });
    setSavingClient(false);
    if (res?.status === "success") {
      ToastMsg("Client updated", "success");
      setEditingClient(false);
      loadClient();
    } else {
      ToastMsg(res?.message || "Failed to update client", "error");
    }
  };

  if (loading) {
    return (
      <ContainerCard>
        <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Loading...</div>
      </ContainerCard>
    );
  }
  if (!client) {
    return (
      <ContainerCard>
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <AlertCircle className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-500">Client not found</p>
          <Button variant="outline" onClick={() => navigate("/onboarded")}>Back to list</Button>
        </div>
      </ContainerCard>
    );
  }

  const done    = milestones.filter(m => m.status === "Done").length;
  const total   = milestones.length;
  const percent = total ? Math.round((done / total) * 100) : 0;
  // Server-derived: a milestone held on the client's side has its clock
  // paused, so this is not a date comparison the screen can do itself.
  const overdue = milestones.filter(m => m.is_overdue).length;

  return (
    <ContainerCard>
      <div className="space-y-4 p-2">

        {/* ── Top bar ──────────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/onboarded")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{client.org_name}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[client.onboarding_status] || "bg-gray-100 text-gray-600"}`}>
                {client.onboarding_status}
              </span>
              {client.entry_type === "direct" && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  Direct Entry
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {[client.city, client.state].filter(Boolean).join(", ")}
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={loadClient} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────── */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Overview                                              */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Overview" && (
          <div className="grid grid-cols-3 gap-4">
            {/* Client Details */}
            <div className={`${card} p-5 col-span-2 space-y-4`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Client Details</p>
                {!editingClient ? (
                  <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={startEditClient}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-7 text-xs gap-1" onClick={saveClientDetails} disabled={savingClient}>
                      <Check className="h-3.5 w-3.5" /> {savingClient ? "Saving…" : "Save"}
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setEditingClient(false)}>
                      <X className="h-3.5 w-3.5" /> Cancel
                    </Button>
                  </div>
                )}
              </div>

              {!editingClient ? (
                <>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <LabelVal label="Organization"  value={client.org_name} />
                    <LabelVal label="Client Type"   value={client.client_type} />
                    <LabelVal label="City"          value={client.city} />
                    <LabelVal label="State"         value={State.getStatesOfCountry("IN").find(s => s.isoCode === client.state)?.name || client.state} />
                    <LabelVal label="Onboarded"     value={fmtDate(client.onboarded_date)} />
                    <LabelVal label="Go Live"       value={fmtDate(client.go_live_date)} />
                    <LabelVal label="Assigned To"   value={client.assigned_to_name} />
                    <LabelVal label="Impl. Owner"   value={client.implementation_owner_name} />
                  </div>
                  {client.remarks && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Remarks</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-0.5">{client.remarks}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <LabelInput label="Organization" required>
                    <Input value={editClientForm.org_name} onChange={e => setEditClientForm(p => ({ ...p, org_name: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Client Type">
                    <Select showSearch allowClear optionFilterProp="label" className="w-full"
                      value={editClientForm.client_type || undefined}
                      onChange={v => setEditClientForm(p => ({ ...p, client_type: v || "" }))}
                      options={clientTypes.map(t => ({ value: t.name, label: t.name }))} />
                  </LabelInput>
                  <LabelInput label="State">
                    <Select showSearch allowClear optionFilterProp="label" className="w-full"
                      value={editClientForm.state || undefined}
                      onChange={v => setEditClientForm(p => ({ ...p, state: v || "", city: "" }))}
                      options={State.getStatesOfCountry("IN").map(s => ({ value: s.isoCode, label: s.name }))} />
                  </LabelInput>
                  <LabelInput label="City">
                    <Select showSearch allowClear optionFilterProp="label" className="w-full"
                      value={editClientForm.city || undefined}
                      onChange={v => setEditClientForm(p => ({ ...p, city: v || "" }))}
                      options={editClientForm.state ? City.getCitiesOfState("IN", editClientForm.state).map(c => ({ value: c.name, label: c.name })) : []} />
                  </LabelInput>
                  <LabelInput label="Onboarded Date">
                    <Input type="date" value={editClientForm.onboarded_date} onChange={e => setEditClientForm(p => ({ ...p, onboarded_date: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Go Live Date">
                    <Input type="date" value={editClientForm.go_live_date} onChange={e => setEditClientForm(p => ({ ...p, go_live_date: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Onboarding Status">
                    <Select className="w-full" value={editClientForm.onboarding_status}
                      onChange={v => setEditClientForm(p => ({ ...p, onboarding_status: v }))}
                      options={["In Progress","Go Live","Completed","Suspended"].map(v => ({ value: v, label: v }))} />
                  </LabelInput>
                  <LabelInput label="Assigned To">
                    <Select showSearch allowClear optionFilterProp="label" className="w-full"
                      value={editClientForm.assigned_to_id || undefined}
                      onChange={v => setEditClientForm(p => ({ ...p, assigned_to_id: v || "" }))}
                      options={userOptions} />
                  </LabelInput>
                  <LabelInput label="Implementation Owner">
                    <Select showSearch allowClear optionFilterProp="label" className="w-full"
                      value={editClientForm.implementation_owner_id || undefined}
                      onChange={v => setEditClientForm(p => ({ ...p, implementation_owner_id: v || "" }))}
                      options={userOptions} />
                  </LabelInput>
                  <LabelInput label="Address">
                    <Input value={editClientForm.address} onChange={e => setEditClientForm(p => ({ ...p, address: e.target.value }))} />
                  </LabelInput>
                  <div className="col-span-2">
                    <LabelInput label="Remarks">
                      <Textarea rows={2} value={editClientForm.remarks} onChange={e => setEditClientForm(p => ({ ...p, remarks: e.target.value }))} />
                    </LabelInput>
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* Contract */}
              <div className={`${card} p-5 space-y-3`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Contract</p>
                </div>
                {!editingClient ? (
                  <>
                    <LabelVal label="Products Sold"   value={client.product_sold} />
                    <LabelVal label="Contract Value"  value={fmtCurrency(client.contract_value)} />
                    <LabelVal label="Payment Terms"   value={client.payment_terms} />
                  </>
                ) : (
                  <div className="space-y-3">
                    <LabelInput label="Products Sold">
                      <Input value={editClientForm.product_sold} onChange={e => setEditClientForm(p => ({ ...p, product_sold: e.target.value }))} />
                    </LabelInput>
                    <LabelInput label="Contract Value (₹)">
                      <Input type="number" value={editClientForm.contract_value} onChange={e => setEditClientForm(p => ({ ...p, contract_value: e.target.value }))} />
                    </LabelInput>
                    <LabelInput label="Payment Terms">
                      <Select className="w-full" allowClear value={editClientForm.payment_terms || undefined}
                        onChange={v => setEditClientForm(p => ({ ...p, payment_terms: v || "" }))}
                        options={["Annual","Half-yearly","Quarterly","Monthly","One-time"].map(v => ({ value: v, label: v }))} />
                    </LabelInput>
                  </div>
                )}
              </div>

              {/* Milestone progress */}
              <div className={`${card} p-5 space-y-3`}>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Implementation</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{done}/{total} milestones</span>
                  <span className="text-blue-600 dark:text-blue-400 font-bold">{percent}%</span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${percent}%` }} />
                </div>
                {overdue > 0 && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {overdue} milestone{overdue === 1 ? "" : "s"} past the agreed date
                  </p>
                )}
              </div>

              {/* Latest AMC */}
              {client.amc?.id && (
                <div className={`${card} p-5 space-y-3`}>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Current AMC</p>
                  <LabelVal label="AMC Value"   value={fmtCurrency(client.amc.amc_value)} />
                  <LabelVal label="Valid Until" value={fmtDate(client.amc.amc_end_date)} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 inline-block ${AMC_COLOR[client.amc.payment_status] || "bg-gray-100 text-gray-600"}`}>
                      {(() => {
                        const left = daysLeft(client.amc.amc_end_date);
                        return left < 0   ? `Overdue ${Math.abs(left)}d` :
                               left <= 30 ? `Expiring in ${left}d`        :
                               client.amc.payment_status;
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Handover — what sales handed over when the deal was won */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Handover" && (
          <div className="space-y-4">
            {!client?.cre_handover ? (
              <div className={`${card} p-8 text-center`}>
                <ClipboardList className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No sales handover recorded</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {client?.customer_id
                    ? "This deal was won before the handover step was in place. Fill it in on the CRE Handover tab of the CRM lead and it will appear here."
                    : "This client was added directly, with no CRM lead behind it, so there is no sales handover to show."}
                </p>
              </div>
            ) : (
              <>
                <div className={`${card} p-4 flex items-start justify-between gap-4 flex-wrap`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                      <ClipboardList className="h-4 w-4 text-indigo-500" /> Sales to CRE Handover
                    </p>
                    {/* Where it is edited, said once and plainly. Otherwise the
                        first instinct on finding a wrong value here is to look
                        for an Edit button that will never exist. */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Recorded by sales when the deal was won. Read-only here — it is edited on the
                      CRE Handover tab of the CRM lead.
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Recorded</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                      {client.cre_handover.filled_count} / {client.cre_handover.total_count}
                    </p>
                  </div>
                </div>

                {(client.cre_handover.groups || []).map(group => (
                  <div key={group.group} className={`${card} overflow-hidden`}>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{group.group}</p>
                      {group.help_text && (
                        <p className="text-[11px] text-gray-400 mt-0.5">{group.help_text}</p>
                      )}
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      {(group.fields || []).map(f => (
                        <div key={f.key} className={f.type === "multi" || f.type === "textarea" ? "md:col-span-2 space-y-1" : "space-y-1"}>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {f.label}
                            {/* Provenance, not decoration: a CRE who disagrees
                                with a value has to know which record to go and
                                fix, and it is not this one. */}
                            {f.is_auto && f.source && (
                              <span className="ml-1 text-[10px] px-1 py-px rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 inline-flex items-center gap-0.5">
                                <Link2 className="h-2 w-2" /> {f.source}
                              </span>
                            )}
                            {f.is_retired && (
                              <span className="ml-1 text-[10px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400">
                                archived
                              </span>
                            )}
                          </p>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            <HandoverValue f={f} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Who to actually ring. The contacts tab holds everyone; this
                    says which of them decides and which is only informed. */}
                {!!(client.cre_handover.spocs || []).length && (
                  <div className={`${card} overflow-hidden`}>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" /> SPOCs
                      </p>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {client.cre_handover.spocs.map(sp => (
                        <div key={sp.id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {sp.name || "Unnamed contact"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {[sp.designation, sp.contact_number, sp.email].filter(Boolean).join(" · ") || "No details recorded"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {sp.spoc_type && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                                {sp.spoc_type}
                              </span>
                            )}
                            {sp.authority && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {sp.authority}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Modules (from frozen quotation plan)                  */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Modules" && (
          <div className="space-y-4">
            {!client?.quotation_plan ? (
              <div className={`${card} p-8 text-center`}>
                <Package className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No quotation linked</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This client was onboarded without a frozen quotation. Modules appear here once a plan is finalized in the CRM quote.
                </p>
              </div>
            ) : (
              <>
                {/* Where this came from. A plan resolved off the lead is a good
                    guess, not a recorded fact, and the difference matters if
                    someone is about to bill from it. */}
                {client.plan_source === 'lead' && (
                  <div className={`rounded-lg border px-4 py-3 text-xs flex items-start gap-2 ${
                    client.plan_value_matches
                      ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/10 text-blue-800 dark:text-blue-300'
                      : 'border-amber-300 bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-300'
                  }`}>
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      Not linked to this record — taken from the accepted quotation on the
                      CRM lead{client.plan_value_matches ? '' : ', and its total does not match the contract value below'}.
                      {client.plan_value_matches
                        ? ' Its total matches the contract value, so this is almost certainly the plan sold.'
                        : ' Check it is the right quotation before billing from it.'}
                    </span>
                  </div>
                )}

                {/* Plan header */}
                <div className={`${card} p-5`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frozen Plan</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white mt-1">
                        {client.quotation_plan?.plan_label}
                        {client.quotation_plan?.package_title && (
                          <span className="text-gray-500 dark:text-gray-400 font-normal"> · {client.quotation_plan.package_title}</span>
                        )}
                      </p>
                      {client.quotation_plan?.quote_number && (
                        <button
                          onClick={() => navigate(`/quotations/${client.quotation_plan.quote_id}`)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" /> {client.quotation_plan.quote_number}
                        </button>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Grand Total</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {fmtCurrency(client.quotation_plan?.grand_total)}
                      </p>
                      {client.quotation_plan?.amc_rate && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          AMC: <span className="font-semibold text-gray-700 dark:text-gray-200">{client.quotation_plan.amc_rate}%</span> p.a.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {(() => {
                  const modules = client.modules || [];
                  const features = modules.filter(m => m.kind === 'feature');
                  // Custom lines are priced charges too (qty x rate, in the total).
                  const addons   = modules.filter(m => m.kind === 'add_on' || m.kind === 'line');

                  return (
                    <>
                      {/* Features (included services) */}
                      <div className={`${card} overflow-hidden`}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-emerald-600" />
                            Included Modules &amp; Features ({features.length})
                          </h3>
                        </div>
                        {features.length === 0 ? (
                          <div className="px-5 py-6 text-xs text-gray-500 dark:text-gray-400 text-center">
                            No included modules on this plan.
                          </div>
                        ) : (
                          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                            {features.map(f => (
                              <li key={f.id} className="px-5 py-3 flex items-start gap-3">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{f.name}</p>
                                  {f.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{f.description}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Add-ons / other charges */}
                      <div className={`${card} overflow-hidden`}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <PlusCircle className="h-4 w-4 text-blue-600" />
                            Add-ons &amp; Other Charges ({addons.length})
                          </h3>
                        </div>
                        {addons.length === 0 ? (
                          <div className="px-5 py-6 text-xs text-gray-500 dark:text-gray-400 text-center">
                            No add-ons on this plan.
                          </div>
                        ) : (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs text-gray-600 dark:text-gray-400">
                              <tr>
                                <th className="px-5 py-2 text-left font-medium">Name</th>
                                <th className="px-5 py-2 text-left font-medium">Description</th>
                                <th className="px-5 py-2 text-right font-medium">Price</th>
                                <th className="px-5 py-2 text-left font-medium">Unit</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {addons.map(a => (
                                <tr key={a.id}>
                                  <td className="px-5 py-2.5 font-medium text-gray-900 dark:text-white">{a.name}</td>
                                  <td className="px-5 py-2.5 text-gray-600 dark:text-gray-400">{a.description || '—'}</td>
                                  <td className="px-5 py-2.5 text-right text-gray-900 dark:text-white">
                                    {a.kind === 'line' ? fmtCurrency(Number(a.quantity || 1) * Number(a.price || 0)) : fmtCurrency(a.price)}
                                  </td>
                                  <td className="px-5 py-2.5 text-gray-600 dark:text-gray-400">
                                    {a.kind === 'line'
                                      ? `Custom · ${Number(a.quantity || 1)} × ${fmtCurrency(a.price)} · in total`
                                      : (a.unit_label || '—')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Product Payments                                      */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Product Payments" && (
          <div className="space-y-4">
            <ReceiptSummary
              title="Sold Product"
              value={productSummary?.contract_value ?? client?.contract_value}
              paid={productSummary?.contract_paid ?? client?.contract_paid}
              pending={productSummary?.contract_pending ?? client?.contract_pending}
              valueLabel="Contract Value"
              fmtCurrency={fmtCurrency}
            />

            <div className={`${card} overflow-hidden`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-primary" />
                  Payment Receipts ({productReceipts.length})
                </h3>
                {canCreate && (
                  <Button size="sm" onClick={() => {
                    if (showAddProductReceipt) {
                      setShowAddProductReceipt(false);
                      setEditingProductReceiptId(null);
                      setProductReceiptForm(EMPTY_RECEIPT);
                    } else {
                      setEditingProductReceiptId(null);
                      setProductReceiptForm(EMPTY_RECEIPT);
                      setShowAddProductReceipt(true);
                    }
                  }}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> {showAddProductReceipt ? "Cancel" : "Add Receipt"}
                  </Button>
                )}
              </div>

              {showAddProductReceipt && (
                <ReceiptForm
                  form={productReceiptForm}
                  setForm={setProductReceiptForm}
                  onSave={saveProductReceipt}
                  onCancel={() => { setShowAddProductReceipt(false); setEditingProductReceiptId(null); setProductReceiptForm(EMPTY_RECEIPT); }}
                  isEdit={!!editingProductReceiptId}
                />
              )}

              <ReceiptTable
                receipts={productReceipts}
                fmtDate={fmtDate}
                fmtCurrency={fmtCurrency}
                onEdit={canEdit ? startEditProductReceipt : null}
                onDelete={canDelete ? deleteProductReceipt : null}
              />
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: AMC & Renewals                                        */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "AMC & Renewals" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => {
                // Prefill AMC % from the frozen plan's negotiated rate (if present)
                const planRate = client?.quotation_plan?.amc_rate;
                const cv = parseFloat(client?.contract_value || 0);
                const pct = parseFloat(planRate);
                if (planRate && !isNaN(pct) && cv) {
                  setAmcForm(p => ({
                    ...p,
                    amc_percentage: String(pct),
                    amc_value:      (cv * pct / 100).toFixed(2),
                  }));
                }
                cancelEditAmc();
                setShowAddAmc(true);
              }} size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add AMC Record
              </Button>
            </div>

            {/* Add AMC form */}
            {showAddAmc && (
              <div className={`${card} p-5`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">New AMC Record</p>
                    {client?.quotation_plan?.amc_rate && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5">
                        Prefilled from quotation: <b>{client.quotation_plan.amc_rate}%</b> (negotiated rate on {client.quotation_plan.quote_number})
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Product Cost: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtCurrency(client?.contract_value)}</span>
                  </p>
                </div>
                <AMCFormFields form={amcForm} setForm={setAmcForm} contractValue={client?.contract_value} />
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={saveAmc}>Save AMC</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddAmc(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* AMC Records */}
            {amcRecords.length === 0 ? (
              <div className={`${card} p-8 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500`}>
                <Calendar className="h-8 w-8 opacity-30" />
                <p className="text-sm">No AMC records yet</p>
              </div>
            ) : (
              amcRecords.map(amc => {
                const left = daysLeft(amc.amc_end_date);
                return (
                  <div key={amc.id} className={`${card} p-5 space-y-3`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{fmtCurrency(amc.amc_value)}</span>
                        {amc.amc_percentage && (
                          <span className="text-[11px] text-gray-500 dark:text-gray-400">({amc.amc_percentage}% of product cost)</span>
                        )}
                        {(() => {
                          // "Overdue" means unpaid past the end date. A paid (or waived)
                          // record whose term is over has simply ended.
                          const settled = amc.payment_status === "Paid" || amc.payment_status === "Waived";
                          const [label, cls] =
                            left !== null && left < 0
                              ? (settled ? [`Ended · ${amc.payment_status}`, AMC_COLOR.Waived]
                                         : [`Overdue ${Math.abs(left)}d`, AMC_COLOR.Overdue])
                              : left !== null && left <= 30
                                ? [`Expiring in ${left}d`, AMC_COLOR[amc.payment_status] || AMC_COLOR.Waived]
                                : [amc.payment_status, AMC_COLOR[amc.payment_status] || AMC_COLOR.Waived];
                          return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
                        })()}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => (editingAmcId === amc.id ? cancelEditAmc() : startEditAmc(amc))}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> {editingAmcId === amc.id ? "Cancel Edit" : "Edit"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { cancelEditAmc(); setShowRenewId(amc.id); setRenewForm({ new_end_date: "", renewal_value: amc.amc_value, remarks: "" }); }}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Renew
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => loadRenewalHistory(amc.id)}>
                          History
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-6 gap-4">
                      <LabelVal label="Start"         value={fmtDate(amc.amc_start_date)} />
                      <LabelVal label="End"           value={fmtDate(amc.amc_end_date)} />
                      <LabelVal label="Payment Terms" value={amc.payment_terms} />
                      <LabelVal label="Reminder"      value={`${amc.renewal_reminder_days} days before`} />
                      <LabelVal label="Paid"          value={fmtCurrency(amc.amc_paid)} />
                      <LabelVal label="Pending"       value={fmtCurrency(amc.amc_pending)} />
                    </div>

                    {/* Edit AMC form inline */}
                    {editingAmcId === amc.id && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Edit AMC Record</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Product Cost: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtCurrency(client?.contract_value)}</span>
                          </p>
                        </div>
                        <AMCFormFields form={editAmcForm} setForm={setEditAmcForm} contractValue={client?.contract_value} />
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" onClick={updateAmc}>Update AMC</Button>
                          <Button size="sm" variant="outline" onClick={cancelEditAmc}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* ── AMC Receipts ── */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          className="text-xs font-semibold text-primary hover:underline flex items-center gap-1.5"
                          onClick={() => {
                            const next = expandedAmcId === amc.id ? null : amc.id;
                            setExpandedAmcId(next);
                            if (next && !amcReceiptsLoaded[amc.id]) loadAmcReceipts(amc.id);
                          }}
                        >
                          <Receipt className="h-3.5 w-3.5" />
                          {expandedAmcId === amc.id ? "Hide Receipts" : "View / Add Receipts"}
                        </button>
                        {expandedAmcId === amc.id && canCreate && (
                          <Button size="sm" variant="outline" onClick={() => {
                            if (showAmcReceiptFor === amc.id) {
                              setShowAmcReceiptFor(null);
                              setEditingAmcReceiptId(null);
                              setAmcReceiptForm(EMPTY_RECEIPT);
                            } else {
                              setEditingAmcReceiptId(null);
                              setAmcReceiptForm(EMPTY_RECEIPT);
                              setShowAmcReceiptFor(amc.id);
                            }
                          }}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> {showAmcReceiptFor === amc.id ? "Cancel" : "Add Receipt"}
                          </Button>
                        )}
                      </div>

                      {expandedAmcId === amc.id && (
                        <div className="mt-3 space-y-3">
                          {showAmcReceiptFor === amc.id && (
                            <ReceiptForm
                              form={amcReceiptForm}
                              setForm={setAmcReceiptForm}
                              onSave={() => saveAmcReceipt(amc.id)}
                              onCancel={() => { setShowAmcReceiptFor(null); setEditingAmcReceiptId(null); setAmcReceiptForm(EMPTY_RECEIPT); }}
                              isEdit={!!editingAmcReceiptId}
                            />
                          )}
                          <ReceiptTable
                            receipts={amcReceipts[amc.id]?.data || []}
                            fmtDate={fmtDate}
                            fmtCurrency={fmtCurrency}
                            onEdit={canEdit ? (r) => startEditAmcReceipt(r, amc.id) : null}
                            onDelete={canDelete ? (rid) => deleteAmcReceipt(rid, amc.id) : null}
                          />
                        </div>
                      )}
                    </div>

                    {/* Renew form inline */}
                    {showRenewId === amc.id && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Renew AMC</p>
                        <div className="grid grid-cols-3 gap-4">
                          <LabelInput label="New End Date" required>
                            <Input type="date" value={renewForm.new_end_date} onChange={e => setRenewForm(p => ({ ...p, new_end_date: e.target.value }))} />
                          </LabelInput>
                          <LabelInput label="Renewal Value (₹)">
                            <Input type="number" value={renewForm.renewal_value} onChange={e => setRenewForm(p => ({ ...p, renewal_value: e.target.value }))} />
                          </LabelInput>
                          <LabelInput label="Remarks">
                            <Input placeholder="Optional" value={renewForm.remarks} onChange={e => setRenewForm(p => ({ ...p, remarks: e.target.value }))} />
                          </LabelInput>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => renewAmc(amc.id)}>Confirm Renewal</Button>
                          <Button size="sm" variant="outline" onClick={() => setShowRenewId(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Renewal history */}
                    {renewalHistory[amc.id] && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Renewal History</p>
                        {renewalHistory[amc.id].length === 0 ? (
                          <p className="text-xs text-gray-400">No renewals yet</p>
                        ) : (
                          <div className="space-y-2">
                            {renewalHistory[amc.id].map(r => (
                              <div key={r.id} className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 rounded-lg px-3 py-2">
                                <span>{fmtDate(r.previous_end)} → {fmtDate(r.new_end)}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{fmtCurrency(r.renewal_value)}</span>
                                <span>by {r.renewed_by_name}</span>
                                {r.remarks && <span className="italic">{r.remarks}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Milestones                                            */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Milestones" && (
          <MilestonesPanel
            clientId={id}
            userOptions={userOptions}
            onChanged={loadClient}
          />
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Contacts                                              */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Contacts" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={openAddContact}>
                <Plus className="h-4 w-4 mr-1" /> Add Contact
              </Button>
            </div>

            {showAddContact && (
              <div className={`${card} p-4`}>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                  {editingContactId ? "Edit Contact" : "New Contact"}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <LabelInput label="Name" required>
                    <Input placeholder="Contact name" value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Designation">
                    <Input placeholder="e.g. IT Manager" value={contactForm.designation} onChange={e => setContactForm(p => ({ ...p, designation: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Phone">
                    <Input placeholder="Mobile" value={contactForm.contact_number} onChange={e => setContactForm(p => ({ ...p, contact_number: e.target.value }))} />
                  </LabelInput>
                  <LabelInput label="Email">
                    <Input type="email" placeholder="Email" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} />
                  </LabelInput>
                  <div className="flex items-center gap-2 pt-5">
                    <input type="checkbox" id="is_primary" checked={contactForm.is_primary} onChange={e => setContactForm(p => ({ ...p, is_primary: e.target.checked }))} />
                    <label htmlFor="is_primary" className="text-sm text-gray-700 dark:text-gray-300">Primary contact</label>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={saveContact}>{editingContactId ? "Update" : "Add"}</Button>
                  <Button size="sm" variant="outline" onClick={cancelContact}>Cancel</Button>
                </div>
              </div>
            )}

            {contacts.length === 0 ? (
              <div className={`${card} p-8 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500`}>
                <User className="h-8 w-8 opacity-30" />
                <p className="text-sm">No contacts yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {contacts.map(c => (
                  <div key={c.id} className={`${card} p-4 flex items-start gap-3`}>
                    <div className="h-9 w-9 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {(c.name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{c.name}</p>
                        {c.is_primary && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">Primary</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{c.designation || "—"}</p>
                      {c.contact_number && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3" />{c.contact_number}
                        </p>
                      )}
                      {c.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Mail className="h-3 w-3" />{c.email}
                        </p>
                      )}
                    </div>
                    <div className="flex items-start gap-0.5 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-gray-400 hover:text-blue-600"
                        onClick={() => openEditContact(c)} title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600"
                        onClick={() => deleteContact(c.id)} title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TAB: Support                                               */}
        {/* ══════════════════════════════════════════════════════════ */}
        {activeTab === "Notes" && (
          <div className="space-y-4">
            {/* Add note */}
            <div className={`${card} p-4 space-y-3`}>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Add a note</p>
              <Textarea
                rows={3}
                value={noteDraft}
                onChange={e => setNoteDraft(e.target.value)}
                placeholder="Type a note about this onboarded client..."
              />
              <div className="flex justify-end">
                <Button size="sm" className="gap-1.5" disabled={savingNote || !noteDraft.trim()} onClick={addNote}>
                  <Plus className="h-3.5 w-3.5" /> {savingNote ? "Saving..." : "Add Note"}
                </Button>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              {notes.length} note{notes.length !== 1 ? "s" : ""}
            </p>

            {notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500 gap-2">
                <StickyNote className="h-8 w-8 opacity-30" />
                <p className="text-sm">No notes yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map(n => (
                  <div key={n.id} className={`${card} p-4`}>
                    {editingNoteId === n.id ? (
                      <div className="space-y-2">
                        <Textarea rows={3} value={editNoteText} onChange={e => setEditNoteText(e.target.value)} />
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" className="h-7 text-xs"
                            onClick={() => { setEditingNoteId(null); setEditNoteText(""); }}>
                            <X className="h-3 w-3" /> Cancel
                          </Button>
                          <Button size="sm" className="h-7 text-xs" onClick={() => saveEditedNote(n.id)}>
                            <Check className="h-3 w-3" /> Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                            {n.note}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => { setEditingNoteId(n.id); setEditNoteText(n.note); }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                              onClick={() => deleteNote(n.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2 flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" /> {n.author_name || "—"}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {n.created_date
                              ? new Date(n.created_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "Support" && (
          <div className="space-y-4">
            {/* Add ticket form */}
            {showAddTicket && (
              <div className={`${card} p-4 space-y-3`}>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">New Support Ticket</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Title <span className="text-red-500">*</span></label>
                    <Input value={ticketForm.title} onChange={e => setTicketForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="Describe the issue briefly" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Priority</label>
                    <Select className="w-full" size="middle" value={ticketForm.priority}
                      onChange={v => setTicketForm(p => ({ ...p, priority: v }))}
                      options={["Low","Medium","High","Critical"].map(x => ({ value: x, label: x }))} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Assigned To</label>
                    <Select className="w-full" size="middle" allowClear showSearch optionFilterProp="label"
                      value={ticketForm.assigned_to_id || undefined}
                      onChange={v => setTicketForm(p => ({ ...p, assigned_to_id: v || "" }))}
                      options={users.map(u => ({ value: String(u.id), label: `${u.login_fname||""} ${u.login_lname||""}`.trim() }))} />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
                    <Textarea rows={3} value={ticketForm.description}
                      onChange={e => setTicketForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Detailed description..." />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowAddTicket(false)}>Cancel</Button>
                  <Button size="sm" onClick={async () => {
                    if (!ticketForm.title.trim()) { ToastMsg("Title is required", "error"); return; }
                    const res = await create(ONBOARDED_TICKETS_URL(id), {
                      title: ticketForm.title.trim(),
                      description: ticketForm.description,
                      priority: ticketForm.priority,
                      assigned_to_id: ticketForm.assigned_to_id || null,
                    });
                    if (res?.status === "success") {
                      ToastMsg("Ticket created", "success");
                      setShowAddTicket(false);
                      setTicketForm({ title: "", description: "", priority: "Medium", assigned_to_id: "" });
                      loadTickets();
                    } else { ToastMsg(res?.message || "Failed to create ticket", "error"); }
                  }}>Create Ticket</Button>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">{tickets.length} ticket{tickets.length !== 1 ? "s" : ""}</p>
              {!showAddTicket && (
                <Button size="sm" className="gap-1.5" onClick={() => setShowAddTicket(true)}>
                  <Plus className="h-3.5 w-3.5" /> New Ticket
                </Button>
              )}
            </div>

            {/* Ticket list */}
            {tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500 gap-2">
                <LifeBuoy className="h-8 w-8 opacity-30" />
                <p className="text-sm">No support tickets yet</p>
              </div>
            ) : (
              <div className={`${card} overflow-hidden`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                      {["Title","Priority","Status","Assigned To","Created",""].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {tickets.map(t => (
                      <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        {editingTicketId === t.id ? (
                          <>
                            <td className="px-4 py-3" colSpan={4}>
                              <div className="grid grid-cols-2 gap-2">
                                <Input className="col-span-2 h-8" value={editTicketForm.title}
                                  onChange={e => setEditTicketForm(p => ({ ...p, title: e.target.value }))} />
                                <Select size="small" value={editTicketForm.priority}
                                  onChange={v => setEditTicketForm(p => ({ ...p, priority: v }))}
                                  options={["Low","Medium","High","Critical"].map(x => ({ value: x, label: x }))} />
                                <Select size="small" value={editTicketForm.ticket_status}
                                  onChange={v => setEditTicketForm(p => ({ ...p, ticket_status: v }))}
                                  options={["Open","In Progress","Resolved","Closed","On Hold"].map(x => ({ value: x, label: x }))} />
                                <Select size="small" allowClear showSearch optionFilterProp="label"
                                  className="col-span-2" value={editTicketForm.assigned_to_id || undefined}
                                  onChange={v => setEditTicketForm(p => ({ ...p, assigned_to_id: v || "" }))}
                                  options={users.map(u => ({ value: String(u.id), label: `${u.login_fname||""} ${u.login_lname||""}`.trim() }))} />
                              </div>
                            </td>
                            <td className="px-4 py-3" colSpan={2}>
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 text-xs" onClick={async () => {
                                  const res = await update(ONBOARDED_TICKET_URL(t.id), editTicketForm);
                                  if (res?.status === "success") {
                                    ToastMsg("Ticket updated", "success");
                                    setEditingTicketId(null);
                                    loadTickets();
                                  } else { ToastMsg("Failed to update", "error"); }
                                }}>Save</Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs"
                                  onClick={() => setEditingTicketId(null)}>Cancel</Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 max-w-xs">
                              <p className="font-medium text-gray-900 dark:text-white truncate">{t.title}</p>
                              {t.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{t.description}</p>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TICKET_PRIORITY_COLOR[t.priority] || "bg-gray-100 text-gray-600"}`}>
                                {t.priority}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TICKET_STATUS_COLOR[t.ticket_status] || "bg-gray-100 text-gray-600"}`}>
                                {t.ticket_status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{t.assigned_to_name || "—"}</td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                              {t.created_date ? new Date(t.created_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => { setEditingTicketId(t.id); setEditTicketForm({ title: t.title, priority: t.priority, ticket_status: t.ticket_status, assigned_to_id: t.assigned_to_id ? String(t.assigned_to_id) : "" }); }}>
                                <Edit2 className="h-3 w-3" /> Edit
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </ContainerCard>
  );
}

// ── Receipt helpers ────────────────────────────────────────────────
function ReceiptSummary({ title, value, paid, pending, valueLabel, fmtCurrency }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">{valueLabel}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-white mt-1 flex items-center gap-1.5">
          <Wallet className="h-4 w-4 text-gray-400" /> {fmtCurrency(value)}
        </p>
        <p className="text-[11px] text-gray-400 mt-0.5">{title}</p>
      </div>
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-900/40 p-4">
        <p className="text-xs text-green-700 dark:text-green-300">Paid</p>
        <p className="text-lg font-bold text-green-800 dark:text-green-200 mt-1">{fmtCurrency(paid)}</p>
      </div>
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-900/40 p-4">
        <p className="text-xs text-amber-700 dark:text-amber-300">Pending</p>
        <p className="text-lg font-bold text-amber-800 dark:text-amber-200 mt-1">{fmtCurrency(pending)}</p>
      </div>
    </div>
  );
}

function AMCFormFields({ form, setForm, contractValue }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <LabelInput label="Start Date" required>
          <Input type="date" value={form.amc_start_date} onChange={e => setForm(p => ({ ...p, amc_start_date: e.target.value }))} />
        </LabelInput>
        <LabelInput label="End Date" required>
          <Input type="date" value={form.amc_end_date} onChange={e => setForm(p => ({ ...p, amc_end_date: e.target.value }))} />
        </LabelInput>
        <LabelInput label="AMC % of Product Cost">
          <Input type="number" step="0.01" placeholder="e.g. 15" value={form.amc_percentage}
            onChange={e => {
              const v = e.target.value;
              const cv = parseFloat(contractValue || 0);
              const pct = parseFloat(v);
              const calc = (!isNaN(pct) && cv) ? (cv * pct / 100).toFixed(2) : "";
              setForm(p => ({ ...p, amc_percentage: v, amc_value: calc || p.amc_value }));
            }} />
        </LabelInput>
        <LabelInput label="AMC Value (₹)">
          <Input type="number" placeholder="0" value={form.amc_value}
            onChange={e => setForm(p => ({ ...p, amc_value: e.target.value, amc_percentage: "" }))} />
        </LabelInput>
        <LabelInput label="Payment Terms">
          <Select className="w-full" value={form.payment_terms} onChange={v => setForm(p => ({ ...p, payment_terms: v }))}
            options={["Annual","Half-yearly","Quarterly","Monthly"].map(v => ({ value: v, label: v }))} />
        </LabelInput>
        <LabelInput label="Payment Status">
          <Select className="w-full" value={form.payment_status} onChange={v => setForm(p => ({ ...p, payment_status: v }))}
            options={["Paid","Pending","Overdue","Waived"].map(v => ({ value: v, label: v }))} />
        </LabelInput>
        <LabelInput label="Reminder (days before expiry)">
          <Input type="number" value={form.renewal_reminder_days} onChange={e => setForm(p => ({ ...p, renewal_reminder_days: e.target.value }))} />
        </LabelInput>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Enter percentage to auto-calculate AMC Value, or enter AMC Value directly.</p>
    </>
  );
}

function ReceiptForm({ form, setForm, onSave, onCancel, isEdit }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <LabelInput label="Receipt Date" required>
          <Input type="date" value={form.receipt_date} onChange={e => setForm(p => ({ ...p, receipt_date: e.target.value }))} />
        </LabelInput>
        <LabelInput label="Amount (₹)" required>
          <Input type="number" inputMode="numeric" placeholder="0" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
        </LabelInput>
        <LabelInput label="Payment Mode">
          <Select className="w-full" value={form.payment_mode} onChange={v => setForm(p => ({ ...p, payment_mode: v }))}
            options={PAYMENT_MODES.map(m => ({ value: m, label: m }))} />
        </LabelInput>
        <LabelInput label="Receipt Number">
          <Input placeholder="e.g. RCP-2026-001" value={form.receipt_number} onChange={e => setForm(p => ({ ...p, receipt_number: e.target.value }))} />
        </LabelInput>
        <LabelInput label="Reference No.">
          <Input placeholder="Cheque/UPI ref" value={form.reference_no} onChange={e => setForm(p => ({ ...p, reference_no: e.target.value }))} />
        </LabelInput>
        <LabelInput label="Notes">
          <Input placeholder="Optional" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        </LabelInput>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave}>{isEdit ? "Update Receipt" : "Save Receipt"}</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function ReceiptTable({ receipts, fmtDate, fmtCurrency, onEdit, onDelete }) {
  if (!receipts || receipts.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-gray-400 dark:text-gray-500">No receipts recorded yet</div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700/40 text-left">
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Date</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Amount</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Mode</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Receipt No.</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Ref</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Notes</th>
            <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {receipts.map(r => (
            <tr key={r.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
              <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">{fmtDate(r.receipt_date)}</td>
              <td className="px-4 py-2.5 font-semibold text-gray-900 dark:text-white">{fmtCurrency(r.amount)}</td>
              <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{r.payment_mode}</td>
              <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{r.receipt_number || "—"}</td>
              <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{r.reference_no || "—"}</td>
              <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{r.notes || "—"}</td>
              <td className="px-4 py-2.5 text-right">
                <div className="flex items-center justify-end gap-3">
                  {onEdit && (
                    <button onClick={() => onEdit(r)} className="text-gray-500 hover:text-primary" title="Edit receipt">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {onDelete && (
                    <button onClick={() => onDelete(r.id)} className="text-red-500 hover:text-red-700" title="Delete receipt">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  {!onEdit && !onDelete && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">read only</span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
